import { Fragment, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, date, titleCase } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';

/* ---------------- trial balance ---------------- */

interface TrialBalance {
  accounts: {
    account_number: string;
    account_name: string;
    account_type: string;
    debit_minor: number;
    credit_minor: number;
    net_minor: number;
  }[];
  totals: { debit_minor: number; credit_minor: number };
  balances: boolean;
}

export function TrialBalancePage() {
  const { currency } = useSession();
  const { data, error, loading } = useApi<TrialBalance>('/v1/gl/trial-balance');

  if (loading) return <Loading rows={8} />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  return (
    <>
      <h1 style={{ marginBottom: 16 }}>Trial Balance</h1>

      {/*
        Stated plainly rather than assumed. If this ever reads "out of
        balance" it is a production incident, and burying it would be worse
        than showing it.
      */}
      <div className="tiles">
        <Tile label="Total debits" value={money(data.totals.debit_minor, currency)} />
        <Tile label="Total credits" value={money(data.totals.credit_minor, currency)} />
        <Tile
          label="Balance"
          value={data.balances ? 'In balance' : 'OUT'}
          tone={data.balances ? 'good' : 'critical'}
          note={
            data.balances
              ? 'debits equal credits'
              : `out by ${money(data.totals.debit_minor - data.totals.credit_minor, currency)}`
          }
        />
      </div>

      <Card flush>
        {data.accounts.length === 0 ? (
          <Empty>Nothing posted yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Account</th>
                <th>Name</th>
                <th>Type</th>
                <th className="num">Debit</th>
                <th className="num">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts.map((a) => (
                <tr key={a.account_number}>
                  <td className="mono">{a.account_number}</td>
                  <td>{a.account_name}</td>
                  <td className="muted">{titleCase(a.account_type)}</td>
                  <td className="num">
                    {Number(a.debit_minor) ? money(a.debit_minor, currency) : <span className="muted">—</span>}
                  </td>
                  <td className="num">
                    {Number(a.credit_minor) ? money(a.credit_minor, currency) : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 650, background: 'var(--surface-sunk)' }}>
                <td colSpan={3}>Total</td>
                <td className="num">{money(data.totals.debit_minor, currency)}</td>
                <td className="num">{money(data.totals.credit_minor, currency)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </Card>
    </>
  );
}

/* ---------------- departmental P&L ---------------- */

interface DeptPL {
  departments: {
    department_id: string | null;
    department_code: string | null;
    department_name: string | null;
    depth: number;
    is_rollup: boolean;
    revenue_minor: number;
    expense_minor: number;
    contribution_minor: number;
    rollup_revenue_minor: number;
    rollup_expense_minor: number;
    rollup_contribution_minor: number;
    lines: { account_number: string; account_name: string; account_type: string; amount_minor: number }[];
  }[];
}

export function DepartmentalPLPage() {
  const { currency } = useSession();
  const { data, error, loading } = useApi<DeptPL>('/v1/gl/departmental-pl');
  const [open, setOpen] = useState<string | null>(null);

  if (loading) return <Loading rows={8} />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  // Own figures, not rollups. A heading's revenue is its children's
  // revenue, so adding both would count the clubhouse twice — which is
  // the exact mistake the leaves-only posting rule exists to prevent, and
  // it would be embarrassing to reintroduce it in the report.
  const totals = data.departments.reduce(
    (a, d) => ({
      revenue: a.revenue + Number(d.revenue_minor),
      expense: a.expense + Number(d.expense_minor),
    }),
    { revenue: 0, expense: 0 },
  );

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Departmental P&amp;L</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 16 }}>
        The report a club board reads every month. Department is a dimension on every ledger
        line, so this is a view of the books rather than a reconstruction.
      </p>

      <div className="tiles">
        <Tile label="Revenue" value={money(totals.revenue, currency)} />
        <Tile label="Expense" value={money(totals.expense, currency)} />
        <Tile
          label="Contribution"
          value={money(totals.revenue - totals.expense, currency)}
          tone={totals.revenue - totals.expense >= 0 ? 'good' : 'critical'}
          note={totals.revenue - totals.expense >= 0 ? 'positive' : 'negative'}
        />
      </div>

      <Card flush>
        {data.departments.length === 0 ? (
          <Empty>No revenue or expense posted yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Department</th>
                <th className="num">Revenue</th>
                <th className="num">Expense</th>
                <th className="num">Contribution</th>
              </tr>
            </thead>
            <tbody>
              {data.departments.map((d) => {
                const key = d.department_code ?? '—';

                // A heading shows the total of everything beneath it and
                // nothing of its own — it cannot have anything of its own.
                // Drawn as a subtotal so a reader is never left wondering
                // whether "Golf" means the golf desk or all of golf.
                const revenue = d.is_rollup ? d.rollup_revenue_minor : d.revenue_minor;
                const expense = d.is_rollup ? d.rollup_expense_minor : d.expense_minor;
                const contribution = d.is_rollup
                  ? d.rollup_contribution_minor
                  : d.contribution_minor;

                return (
                  <Fragment key={key}>
                    <tr
                      onClick={() => !d.is_rollup && setOpen(open === key ? null : key)}
                      style={{
                        cursor: d.is_rollup ? 'default' : 'pointer',
                        background: d.is_rollup ? 'var(--surface-sunk)' : undefined,
                      }}
                    >
                      <td style={{ paddingLeft: 8 + (d.depth ?? 0) * 20 }}>
                        <span className="mono muted">{d.department_code ?? '—'}</span>{' '}
                        {d.is_rollup ? (
                          <strong>{d.department_name}</strong>
                        ) : (
                          (d.department_name ?? 'Unassigned')
                        )}
                        <span className="muted small">
                          {d.is_rollup
                            ? ' · total of everything below'
                            : ` · ${d.lines.length} account${d.lines.length === 1 ? '' : 's'}`}
                        </span>
                      </td>
                      <td className="num">{money(revenue, currency)}</td>
                      <td className="num">{money(expense, currency)}</td>
                      <td className="num">
                        <Money minor={contribution} currency={currency} signed />
                      </td>
                    </tr>
                    {open === key &&
                      d.lines.map((l) => (
                        <tr key={`${key}-${l.account_number}`} style={{ background: 'var(--surface-sunk)' }}>
                          <td className="small" style={{ paddingLeft: 30 }}>
                            <span className="mono muted">{l.account_number}</span> {l.account_name}
                          </td>
                          <td className="num small">
                            {l.account_type === 'revenue' ? money(l.amount_minor, currency) : ''}
                          </td>
                          <td className="num small">
                            {l.account_type === 'expense' ? money(-l.amount_minor, currency) : ''}
                          </td>
                          <td />
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ---------------- periods ---------------- */

interface Period {
  id: string;
  period_number: number;
  starts_on: string;
  ends_on: string;
  status: string;
  fiscal_year: string;
}

interface CloseCheck {
  name: string;
  ok: boolean;
  detail: unknown;
}

export function PeriodsPage() {
  const { currency, atLeast } = useSession();
  const periods = useApi<{ periods: Period[] }>('/v1/periods');
  const recon = useApi<{ subledger_minor: number; control_account_minor: number; variance_minor: number; reconciled: boolean }>(
    atLeast('controller') ? '/v1/reconciliation/ar-control' : null,
  );
  const [checks, setChecks] = useState<{ id: string; checks: CloseCheck[] } | null>(null);

  const close = useMutation(async (id: string) =>
    api<{ checks: CloseCheck[] }>(`/v1/periods/${id}/close`, { method: 'POST', body: {} }),
  );
  const reopen = useMutation(async (id: string, reason: string) =>
    api(`/v1/periods/${id}/reopen`, { method: 'POST', body: { reason } }),
  );

  async function doClose(id: string) {
    const result = await close.run(id);
    if (result) {
      setChecks({ id, checks: result.checks });
      periods.reload();
      recon.reload();
    }
  }

  async function doReopen(id: string) {
    const reason = window.prompt('Why is this period being reopened? This is recorded.');
    if (!reason) return;
    if (await reopen.run(id, reason)) periods.reload();
  }

  return (
    <>
      <h1 style={{ marginBottom: 16 }}>Accounting periods</h1>

      {/*
        The pre-close checks are surfaced whether they pass or fail. A period
        closed over an unbalanced ledger looks finished, which is worse than
        one left open — so the API refuses, and this shows why.
      */}
      <ErrorBanner error={close.error} />
      <ErrorBanner error={reopen.error} />

      {close.error?.code === 'pre_close_checks_failed' && (
        <Card title="Pre-Close Checks">
          <PreCloseChecks
            checks={(close.error.detail as { checks: CloseCheck[] }).checks}
            currency={currency}
          />
        </Card>
      )}

      {checks && (
        <div className="ok-banner">
          <strong>Period closed.</strong> All pre-close checks passed.
        </div>
      )}

      {recon.data && (
        <div className="tiles">
          <Tile label="AR subledger" value={money(recon.data.subledger_minor, currency)} />
          <Tile label="GL control account" value={money(recon.data.control_account_minor, currency)} />
          <Tile
            label="Reconciliation"
            value={recon.data.reconciled ? 'Tied' : 'Variance'}
            tone={recon.data.reconciled ? 'good' : 'critical'}
            note={
              recon.data.reconciled
                ? 'subledger equals the control account'
                : `out by ${money(recon.data.variance_minor, currency)}`
            }
          />
        </div>
      )}

      <Card flush>
        {periods.loading ? (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Period</th>
                <th>From</th>
                <th>To</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(periods.data?.periods ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.fiscal_year} · {p.period_number}
                  </td>
                  <td className="muted">{date(p.starts_on)}</td>
                  <td className="muted">{date(p.ends_on)}</td>
                  <td>
                    <Status
                      tone={p.status === 'open' ? 'good' : p.status === 'locked' ? 'neutral' : 'warning'}
                    >
                      {titleCase(p.status)}
                    </Status>
                  </td>
                  <td className="num">
                    {atLeast('controller') && p.status === 'open' && (
                      <button className="secondary small" onClick={() => doClose(p.id)} disabled={close.busy}>
                        Close
                      </button>
                    )}
                    {atLeast('controller') && p.status === 'closed' && (
                      <button className="secondary small" onClick={() => doReopen(p.id)} disabled={reopen.busy}>
                        Reopen
                      </button>
                    )}
                    {p.status === 'locked' && <span className="muted small">locked after audit</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function PreCloseChecks({
  checks,
  currency,
}: {
  checks: CloseCheck[];
  currency: { code: string; symbol: string | null; exponent: number };
}) {
  const labels: Record<string, string> = {
    ledger_balances: 'The ledger balances for this period',
    no_draft_entries: 'No draft journal entries remain',
    ar_subledger_ties_to_gl: 'The AR subledger ties to the GL control account',
  };
  return (
    <table className="data">
      <tbody>
        {checks.map((c) => (
          <tr key={c.name}>
            <td>{labels[c.name] ?? c.name}</td>
            <td className="num">
              <Status tone={c.ok ? 'good' : 'critical'}>{c.ok ? 'Passed' : 'Failed'}</Status>
            </td>
            <td className="num muted small">{describeDetail(c, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function describeDetail(
  c: CloseCheck,
  currency: { code: string; symbol: string | null; exponent: number },
): string {
  const d = c.detail as Record<string, unknown> | null;
  if (!d) return '';
  if ('out_of_balance_minor' in d) return money(Number(d.out_of_balance_minor), currency);
  if ('variance_minor' in d) return money(Number(d.variance_minor), currency);
  if ('n' in d) return `${d.n}`;
  return '';
}
